import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/site/motion";
import { useT } from "@/i18n";

const Badge = () => (
  <svg viewBox="0 0 200 200" className="h-full w-full animate-spin-slow" aria-hidden="true">
    <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
    <text fill="#C9A24B" fontSize="13.5" letterSpacing="4.6" fontFamily="Montserrat">
      <textPath href="#circ">PRIVATE CLIENT ADVISORY · DISCRETION · DISTINCTION ·</textPath>
    </text>
  </svg>
);

export default function Hero() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 60, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="home-hero" className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-navy text-ivory">
      <motion.div style={{ y: bgY }} className="absolute inset-0" initial={{ scale: 1.25 }} animate={{ scale: 1.08 }} transition={{ duration: 2.6, ease: [0.2, 0.7, 0.2, 1] }}>
        <img src="/img/hero-london.jpg" alt="Tower Bridge and the London skyline at dusk" className="h-full w-full object-cover" fetchpriority="high" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/20 to-transparent" />

      <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.6, delay: 0.9, ease: [0.2, 0.7, 0.2, 1] }} className="absolute bottom-[16%] right-[8%] z-10 hidden h-36 w-36 lg:block">
        <Badge />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="container-lux relative z-10 pb-20 pt-40 md:pb-28">
        <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }} className="eyebrow-light">Immistop · Global Migration Advisory</motion.p>
        <h1 className="h-display mt-8 text-[2.75rem] sm:text-7xl lg:text-[7.2rem]" data-testid="hero-headline">
          <MaskedLines lines={["Your Global Future,", "Handled with", <em key="d" className="font-normal italic text-gold">Distinction.</em>]} delay={0.35} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 1.1 }} className="mt-8 max-w-xl text-base leading-relaxed text-ivory/85 md:text-lg">
          Private immigration advisory for individuals, families and investors who expect discretion, precision and an exceptional standard of care — from first conversation to final settlement.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 1.3 }} className="mt-11 flex flex-col gap-4 sm:flex-row">
          <Link to="/contact" className="btn-gold" data-testid="hero-book-consultation-btn"><span>{t("cta.book")}</span><ArrowRight className="h-4 w-4" /></Link>
          <Link to="/eligibility" className="btn-outline-light" data-testid="hero-check-eligibility-btn">{t("cta.eligibility")}</Link>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 right-6 z-10 hidden items-center gap-4 md:flex lg:right-12">
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-ivory/70">London · 51.5072° N</span>
        <span className="relative h-14 w-px overflow-hidden bg-ivory/20"><span className="absolute inset-x-0 top-0 h-1/2 animate-scroll-cue bg-gold" /></span>
        <ArrowDown className="h-4 w-4 text-gold" />
      </div>
    </section>
  );
}
